import { Icon, Stack, Text } from '@eluve/components';
import { formatDate } from '@eluve/date-utils';
import { useTranslation } from '@eluve/i18n';

import { InlineEdit } from '../InlineEdit';

import { ChatMessage } from './ChatMessageBubble';

export type ChatThread = {
  id: string;
  title?: string;
  description?: string;
  messages?: ChatMessage[];
  createdAt?: string;
  updatedAt?: string;
};

type ChatThreadCardProps = {
  thread: ChatThread;
  onSelectThread: (threadId: string) => void;
  onThreadTitleChange?: (threadId: string, newTitle: string) => void;
};

// Components

export const ChatThreadCard = ({
  thread,
  onSelectThread,
  onThreadTitleChange,
}: ChatThreadCardProps) => {
  const { t } = useTranslation();
  let description = thread.description;
  if (!description && thread.createdAt) {
    description = formatDate.toMMMMdyyyyWithTime(
      new Date(thread.updatedAt ?? thread.createdAt),
    );
  }

  return (
    <Stack
      aria-label={t('chat.threads.conversationAria', {
        title: thread.title ?? t('chat.threads.unnamed'),
      })}
      className="cursor-pointer rounded-lg border border-border-primary bg-bg-primary p-4 hover:border-border-secondary hover:bg-bg-secondary"
      key={thread.id}
      onClick={() => onSelectThread(thread.id)}
      gap={2}
      direction="horizontal"
    >
      <Stack align="stretch" className="min-w-0 flex-1" gap={1}>
        <InlineEdit
          value={thread.title ?? t('chat.threads.unnamed')}
          onSave={(newTitle) => onThreadTitleChange?.(thread.id, newTitle)}
          placeholder={t('chat.threads.titlePlaceholder')}
          className="w-full"
          maxLength={100}
          isReadOnly={!onThreadTitleChange}
        >
          <Text className="truncate">
            {thread.title ?? t('chat.threads.unnamed')}
          </Text>
        </InlineEdit>
        {description && <Text color="tertiary">{description}</Text>}
      </Stack>
      <Icon
        name="ChevronRight"
        size="lg"
        className="ml-auto text-text-supporting"
      />
    </Stack>
  );
};
